/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { strings } from "../../../common/Strings/Strings";
import { getGeneralError } from "../../../util/helperFunctions";
import CloseButton from "../../../components/CloseButton";
import AlertBox, { AlertType } from "../../../components/AlertBox";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal/Modal";
import { SpeciesRequest } from "../../../models/species/SpeciesRequest";
import SpeciesApi from "../../../api/SpeciesApi";
import SpeciesForm from "./SpeciesForm";
import { AdminSpeciesResponse } from "../../../models/species/AdminSpeciesResponse";

interface Props {
  close: (isReload?: boolean) => void;
  isOpen: boolean;
  species?: AdminSpeciesResponse;
}

const EditSpeciesModal: React.FC<Props> = ({
  isOpen,
  close,
  species,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const methods = useForm<SpeciesRequest>();
  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (species) {
      setValue("isProducingAnimal", species.isProducingAnimal);
      setValue("latinName", species.latinName);
      setValue("localizedNames.en", species.localizedNames.en);
      setValue("localizedNames.hu", species.localizedNames.hu);
      setValue("localizedNames.sv", species.localizedNames.sv);
      setValue("petVetDataSpeciesCategory", species.petVetDataSpeciesCategory);
      setValue("swedishSpeciesCategory", species.swedishSpeciesCategory);
    }
  }, [species]);

  const submitForm = async (formData: SpeciesRequest) => {
    if (species) {
      setLoading(true);

      try {
        await SpeciesApi.updateSpecies(formData, species.id);
        reset();
        close(true);
      } catch (err) {
        setError(await getGeneralError(err));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal handleClose={close} show={isOpen}>
      <Modal.Header title={strings.addSpecies} />
      <Modal.Body noSpacing>
        <FormProvider {...methods}>
          <form className="modal-body" id="createNewSpeciesForm">
            <SpeciesForm />
            <AlertBox message={error} type={AlertType.ERROR} />
          </form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="modal-main-button"
          disabled={loading}
          form="createNewSpeciesForm"
          loading={loading}
          onClick={handleSubmit(submitForm)}
          type="submit"
        >
          {strings.save}
        </Button>
        <CloseButton onClick={close} />
      </Modal.Footer>
    </Modal>
  );
};

export default EditSpeciesModal;    